import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { createPortal } from "react-dom";
import PhonePNG from "../../../../assets/Icons/Sidebar/changepassword.png";
import LogOutPNG from "../../../../assets/Icons/Sidebar/logout_purple.png";
import UserIconPNG from "../../../../assets/Icons/addMemebrIcon.png";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../../../Redux/Hooks";

type MenuCoords = {
  top?: number;
  bottom?: number;
  right: number;
};

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const ProfileMenu: React.FC<{
  menuOpen: boolean;
  setMenuOpen: (boolean: boolean) => void;
  onRequestLogout: () => void;
}> = ({ setMenuOpen, menuOpen, onRequestLogout }) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<MenuCoords | null>(null);
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  const isRole4 = user?.role === 4;

  const updateCoords = () => {
    const anchor = document.getElementById("profile-container");
    if (!anchor) return;

    const rect = anchor.getBoundingClientRect();
    const gap = 8;
    const menuHeight = 180;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < menuHeight && rect.top > spaceBelow;
    const right = Math.max(8, window.innerWidth - rect.right);

    setCoords(
      openUp
        ? { bottom: window.innerHeight - rect.top + gap, right }
        : { top: rect.bottom + gap, right }
    );
  };

  useLayoutEffect(() => {
    if (!menuOpen) {
      setCoords(null);
      return;
    }
    updateCoords();
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return;

    window.addEventListener("resize", updateCoords);
    window.addEventListener("scroll", updateCoords, true);

    return () => {
      window.removeEventListener("resize", updateCoords);
      window.removeEventListener("scroll", updateCoords, true);
    };
  }, [menuOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !document.getElementById("profile-container")?.contains(event.target as Node)
      ) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuOpen, setMenuOpen]);

  if (!menuOpen || !coords) return null;

  return createPortal(
    <DropdownMenu
      ref={menuRef}
      style={{
        top: coords.top,
        bottom: coords.bottom,
        right: coords.right,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {!isRole4 && (
        <MenuItem
          type="button"
          onClick={() => {
            setMenuOpen(false);
            navigate("/dashboard/user/changePhoneNumber");
          }}
        >
          <ItemIcon src={PhonePNG} alt="" />
          <ItemName>Change Phone Number</ItemName>
        </MenuItem>
      )}
      <MenuItem
        type="button"
        onClick={() => {
          setMenuOpen(false);
          navigate("/dashboard/user/profile");
        }}
      >
        <ItemIcon src={UserIconPNG} alt="" />
        <ItemName>Profile</ItemName>
      </MenuItem>
      <MenuItem
        type="button"
        onClick={() => {
          setMenuOpen(false);
          onRequestLogout();
        }}
      >
        <ItemIcon src={LogOutPNG} alt="" />
        <ItemName>Log Out</ItemName>
      </MenuItem>
    </DropdownMenu>,
    document.body
  );
};

export default ProfileMenu;

const DropdownMenu = styled.div`
  position: fixed;
  background: #fff;
  width: 240px;
  z-index: 9999;
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid rgba(23, 23, 23, 0.06);
  box-shadow: 0 12px 36px rgba(23, 23, 23, 0.14);
  animation: ${fadeIn} 0.18s ease-out;
`;

const MenuItem = styled.button`
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #171717;
  display: flex;
  flex-direction: row;
  align-items: center;
  text-align: left;
  transition: background 0.15s ease;

  &:not(:last-child) {
    border-bottom: 1px solid rgba(23, 23, 23, 0.06);
  }

  &:hover {
    background: rgba(201, 77, 170, 0.06);
  }
`;

const ItemIcon = styled.img`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
`;

const ItemName = styled.span`
  margin-left: 12px;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
  color: #171717;
`;
